import { renderCell, renderHeader } from '../components/table';
import { useMemo, useContext } from 'react';
import { Flex, Box, Center, Spinner } from '@chakra-ui/react';
import { Wrapper } from '../components/Wrapper';
import { TitleWithTooltip } from '../components/title-with-tooltip';
import { BreakdownBy } from '../components/breakdown-by';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectHireTable,
  selectHireTableLoading,
  selectHireReportsGroup,
} from '@/store/atsReports/hire/selectors';
import { applyGroup } from '@/store/atsReports/hire';
import { secondsToHumanTime } from '@/pages/reports/non-ats-reports/ats/table';
import {
  EmptyDataWrapper,
  isReportTableEmpty,
} from '../components/empty-data-wrapper';
import { ReportSelectionContext } from '@/contextProviders/ReportSelection';
import { useKFlags } from '@/hooks/use-k-flags';
import { useDrillDownHandler } from '../drilldown/use-drilldownHeader';
import { Table } from '@/components/ats';
import { KText } from '@/ui-library/components';
import {
  BREAKDOWN_OPTIONS,
  GROUP_BY_OPTIONS,
} from './requisitions-table-constants';
import {
  EMPLOYMENT_TYPE_CONFIG,
  REQUISITION_TYPE_CONFIG,
} from '@/pages/requisitions/constants';

export interface Option {
  label: string;
  id: number | string;
}

const formatRequisitionType = (type: string) => {
  return (
    REQUISITION_TYPE_CONFIG[type]?.label ||
    EMPLOYMENT_TYPE_CONFIG[type]?.label ||
    type
  );
};

export const RequisitionTable = ({
  groupBy1 = undefined,
  groupBy2 = undefined,
  customData = undefined,
  isReadOnly = false,
  isPrintView = false,
  customBreakdownChange = undefined,
  renderTitle = undefined,
  onDrillDownOpen = (data) => {},
  isStaticReport = false,
  onSortChange = (sortState) => {},
  isEditMode = false,
  tableColStyles = {},
  onTableColDataChange = undefined,
  initialTableColData = undefined,
  skipLocalStorage = false,
  feReportsV3 = false,
  isFullScreen = false,
}) => {
  const dispatch = useDispatch();
  const { feDrilldown } = useKFlags();
  const { consumer } = useContext(ReportSelectionContext);
  const reduxIsLoading = useSelector(selectHireTableLoading);
  const reduxGroup = useSelector(selectHireReportsGroup);
  const reduxHiresTable = useSelector(selectHireTable);

  const isHiresTableLoading = feReportsV3 ? false : reduxIsLoading;
  const group = feReportsV3 ? null : reduxGroup;
  const hiresTable = feReportsV3 ? [] : reduxHiresTable;

  const dataToUse = customData || hiresTable;
  const isDataEmpty = isReportTableEmpty(dataToUse, 'requisitions_opened');
  const { handleDrillDown } = useDrillDownHandler(feDrilldown, onDrillDownOpen);
  const drilldownGroup = {
    group_by_1: feReportsV3 ? groupBy1 : group?.group_by_1,
    group_by_2: feReportsV3 ? groupBy2 : group?.group_by_2,
  };

  const columns = useMemo(() => {
    const header = feReportsV3
      ? groupBy1 || GROUP_BY_OPTIONS[0]
      : groupBy1 || group?.group_by_1 || GROUP_BY_OPTIONS[0];

    const headerColumn = renderHeader({
      accessor: 'header',
      title: header?.label || 'Name',
      width: 280,
      expand: false,
      sort: true,

      renderFooter: () => <KText size="sm-semi-bold">Total</KText>,
      isReadOnly,
      columnConfig: false,
    });

    const customHeaderColumn = {
      ...headerColumn,
      Cell: (props: any) => {
        const formattedValue = formatRequisitionType(props.cell.value);

        const modifiedProps = {
          ...props,
          cell: {
            ...props.cell,
            value: formattedValue,
          },
        };

        return headerColumn.Cell(modifiedProps);
      },
      id: 'header',
      title: header?.label || 'Name',
      columnConfig: false,
      sortType: (rowA: any, rowB: any) => {
        const valueA = formatRequisitionType(rowA?.values?.header) || '';
        const valueB = formatRequisitionType(rowB?.values?.header) || '';

        return String(valueA)?.localeCompare(String(valueB));
      },
    };

    return [
      customHeaderColumn,
      {
        ...renderCell({
          title: 'Requisitions opened',
          accessor: 'requisitions_opened',
          width: 100,
          sort: true,
          render: (_, row) => {
            return (
              <Flex
                _hover={{ textDecoration: 'underline' }}
                cursor={feDrilldown ? 'pointer' : 'default'}
                onClick={() =>
                  handleDrillDown(
                    row,
                    'Requisitions opened',
                    false,
                    drilldownGroup,
                    null,
                    row?.requisitions_opened
                  )
                }
              >
                {row?.requisitions_opened}
              </Flex>
            );
          },
          renderFooter: (info) => {
            const row = info?.rows?.[0]?.original;
            return (
              <Flex
                _hover={{ textDecoration: 'underline' }}
                cursor={feDrilldown ? 'pointer' : 'default'}
                onClick={() =>
                  handleDrillDown(
                    row,
                    'Requisitions opened',
                    true,
                    drilldownGroup,
                    null,
                    row?.requisitions_opened_total
                  )
                }
              >
                <KText size="sm-semi-bold">
                  {row?.requisitions_opened_total}
                </KText>
              </Flex>
            );
          },
        }),
        id: 'requisitions_opened',
        title: 'Requisitions opened',
        columnConfig: true, // This column should be configurable
      },
      {
        ...renderCell({
          title: 'Requisitions filled',
          accessor: 'requisitions_filled',
          width: 100,
          sort: true,
          render: (_, row) => {
            return (
              <Flex
                _hover={{ textDecoration: 'underline' }}
                cursor={feDrilldown ? 'pointer' : 'default'}
                onClick={() =>
                  handleDrillDown(
                    row,
                    'Requisitions filled',
                    false,
                    drilldownGroup,
                    null,
                    row?.requisitions_filled
                  )
                }
              >
                {row?.requisitions_filled}
              </Flex>
            );
          },
          renderFooter: (info) => {
            const row = info?.rows?.[0]?.original;
            return (
              <Flex
                _hover={{ textDecoration: 'underline' }}
                cursor={feDrilldown ? 'pointer' : 'default'}
                onClick={() =>
                  handleDrillDown(
                    row,
                    'Requisitions filled',
                    true,
                    drilldownGroup,
                    null,
                    row?.requisitions_filled_total
                  )
                }
              >
                <KText size="sm-semi-bold">
                  {row?.requisitions_filled_total}
                </KText>
              </Flex>
            );
          },
        }),
        id: 'requisitions_filled',
        title: 'Requisitions filled',
        columnConfig: true, // This column should be configurable
      },
      {
        ...renderCell({
          title: 'Requisitions closed',
          accessor: 'requisitions_closed',
          width: 100,
          sort: true,
          render: (_, row) => {
            return (
              <Flex
                _hover={{ textDecoration: 'underline' }}
                cursor={feDrilldown ? 'pointer' : 'default'}
                onClick={() =>
                  handleDrillDown(
                    row,
                    'Requisitions closed',
                    false,
                    drilldownGroup,
                    null,
                    row?.requisitions_closed
                  )
                }
              >
                {row?.requisitions_closed}
              </Flex>
            );
          },
          renderFooter: (info) => {
            const row = info?.rows?.[0]?.original;
            return (
              <Flex
                _hover={{ textDecoration: 'underline' }}
                cursor={feDrilldown ? 'pointer' : 'default'}
                onClick={() =>
                  handleDrillDown(
                    row,
                    'Requisitions closed',
                    true,
                    drilldownGroup,
                    null,
                    row?.requisitions_closed_total
                  )
                }
              >
                <KText size="sm-semi-bold">
                  {row?.requisitions_closed_total}
                </KText>
              </Flex>
            );
          },
        }),
        id: 'requisitions_closed',
        title: 'Requisitions closed',
        columnConfig: true, // This column should be configurable
      },
      {
        ...renderCell({
          title: 'Avg. time to fill',
          accessor: 'time_to_fill',
          width: 100,
          sort: true,
          render: (_, row) => {
            return (
              <Flex
                _hover={{ textDecoration: 'underline' }}
                cursor={feDrilldown ? 'pointer' : 'default'}
                onClick={() =>
                  handleDrillDown(
                    row,
                    'Avg. time to fill',
                    false,
                    drilldownGroup,
                    null,
                    secondsToHumanTime(
                      row?.time_to_fill / row?.requisitions_filled
                    )
                  )
                }
              >
                {secondsToHumanTime(
                  row?.time_to_fill / row?.requisitions_filled
                )}
              </Flex>
            );
          },
        }),
        id: 'time_to_fill',
        title: 'Avg. time to fill',
        columnConfig: true, // This column should be configurable
        sortType: (rowA, rowB) => {
          const requisitionsClosedA =
            parseInt(rowA.original?.requisitions_filled) || 0;
          const requisitionsClosedB =
            parseInt(rowB.original?.requisitions_filled) || 0;
          const avgA =
            requisitionsClosedA > 0
              ? parseInt(rowA.values?.time_to_fill || 0) / requisitionsClosedA
              : 0;
          const avgB =
            requisitionsClosedB > 0
              ? parseInt(rowB.values?.time_to_fill || 0) / requisitionsClosedB
              : 0;
          return avgA - avgB;
        },
      },
    ];
  }, [groupBy1, groupBy2, feReportsV3, group]); //eslint-disable-line

  // Single onChange handler
  const handleBreakdownChange = (key: string, option: Option) => {
    switch (key) {
      case 'group_by_1':
        dispatch(applyGroup({ key: 'group_by_1', value: option }));

        break;
      case 'group_by_2':
        dispatch(applyGroup({ key: 'group_by_2', value: option }));
        break;
      default:
        break;
    }
  };

  const handleSortChange = (state) => {
    const { sortBy } = state;
    const currentSort = sortBy?.[0];
    onSortChange(currentSort);
  };

  // Configuration array for BreakdownBy components - conditional Redux fallbacks
  const breakdownConfig = [
    {
      key: 'group_by_1',
      prefix: 'Group by',
      options: GROUP_BY_OPTIONS,
      selected: feReportsV3
        ? groupBy1 || GROUP_BY_OPTIONS[0]
        : groupBy1 || group?.group_by_1 || GROUP_BY_OPTIONS[0],
    },
    {
      key: 'group_by_2',
      prefix: 'Breakdown by',
      options: BREAKDOWN_OPTIONS,
      selected: feReportsV3
        ? groupBy2 || BREAKDOWN_OPTIONS[0]
        : groupBy2 || group?.group_by_2 || BREAKDOWN_OPTIONS[0],
    },
  ];

  return (
    <Wrapper role="group" isReadOnly={isReadOnly || isStaticReport}>
      {consumer === 'DEFAULT' && (
        <Flex
          alignItems="flex-start"
          justifyContent="space-between"
          position="sticky"
          top="0"
          zIndex="6"
          bg="white"
        >
          {renderTitle ? (
            renderTitle?.()
          ) : (
            <Box flex="1">
              <TitleWithTooltip
                label="Requisition breakdown"
                metricDefinition="Comprehensive breakdown of requisitions data "
              />
            </Box>
          )}
          <Flex
            gap="8px"
            mr={
              isPrintView
                ? '0'
                : isDataEmpty
                ? '15px'
                : isEditMode
                ? '55px'
                : '35px'
            }
            mb="12px"
          >
            {breakdownConfig.map(({ key, prefix, options, selected }) => (
              <BreakdownBy
                key={key}
                isPortal={!isFullScreen}
                prefix={prefix}
                options={options}
                selected={selected}
                isEditMode={isEditMode}
                onChange={(option: Option) =>
                  customBreakdownChange
                    ? customBreakdownChange(key, option)
                    : handleBreakdownChange(key, option)
                }
              />
            ))}
          </Flex>
        </Flex>
      )}
      {isHiresTableLoading ? (
        <Center minHeight="200px">
          <Spinner color="primary-800" size="sm" />
        </Center>
      ) : (
        <EmptyDataWrapper data={dataToUse} field="requisitions_opened">
          <Table
            tableId="requisition-table"
            skipLocalStorage={skipLocalStorage}
            isColumnLevelSorting
            enableColumnConfig={isEditMode}
            columns={columns}
            data={dataToUse}
            enableFooter={true}
            tableColStyles={tableColStyles}
            defaultSortBy={[{ id: 'header', desc: false }]}
            layoutScrollContainerStyles="c-data-table wide-content table-container"
            enableExpanded={true}
            layoutStyles={{
              borderBottom: 'none',
            }}
            getTableState={handleSortChange}
            onTableColDataChange={onTableColDataChange}
            initialTableColData={initialTableColData}
          />
        </EmptyDataWrapper>
      )}
    </Wrapper>
  );
};
